import React from "react";
import { FileSpreadsheetIcon, Loader2Icon, Trash2Icon, UploadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { useT } from "@/i18n";

type FileUploadSectionProps = {
  onFileSelect: (file: File | null) => void;
  isValidating: boolean;
  currentFile: File | null;
};

export function FileUploadSection({
  onFileSelect,
  isValidating,
  currentFile,
}: FileUploadSectionProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { t } = useT();

  const handleUploadClick = () => {
    if (!isValidating) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    onFileSelect(file);
  };

  const handleDelete = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onFileSelect(null);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isValidating) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isValidating) return;

    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith(".xlsx")) {
      onFileSelect(file);
    }
  };

  if (isValidating) {
    return (
      <div
        role="status"
        aria-label={t("common.loading")}
        className="flex items-center justify-center rounded-lg border border-dashed border-input px-2 py-10 text-muted-foreground"
      >
        <Loader2Icon className="size-5 animate-spin" />
      </div>
    );
  }

  if (currentFile) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 items-center gap-x-4">
          <span className="shrink-0 font-label text-muted-foreground">
            {t("budget.uploadedFile")}
          </span>
          <div className="flex min-w-0 items-center gap-x-2">
            <FileSpreadsheetIcon className="size-4 shrink-0 text-muted-foreground" />
            <span title={currentFile.name} dir="auto" className="truncate">
              {currentFile.name}
            </span>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={handleDelete}
        >
          <Trash2Icon data-icon="inline-start" />
          {t("budget.deleteFile")}
        </Button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={cn(
          "flex w-full items-center justify-center gap-x-2 rounded-lg border border-dashed border-input px-3 py-6 text-sm text-muted-foreground transition-colors outline-none hover:border-primary hover:text-primary focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
          isDragging && "border-primary bg-primary/5 text-primary",
        )}
        onClick={handleUploadClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <UploadIcon className="size-4" />
        <span>{t("budget.uploadYourFile")}</span>
      </button>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".xlsx"
        onChange={handleFileChange}
        disabled={isValidating}
      />
    </>
  );
}
